import { User, LoginResponse } from '../types';
import { API_CONFIG } from '../config/api';

class AuthService {
    private accessToken: string | null = null;
    private refreshToken: string | null = null;

    async adminLogin(email: string, password: string, captchaToken?: string): Promise<LoginResponse> {
        try {
            const requestBody: any = { email, password };
            if (captchaToken) {
                requestBody.captchaToken = captchaToken;
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ADMIN_LOGIN}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const data: LoginResponse = await response.json();

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Access denied. Admin privileges required.');
                } else if (response.status === 401) {
                    throw new Error(data.message || 'Invalid email or password');
                } else if (response.status === 429) {
                    throw new Error('Too many login attempts. Please try again later.');
                } else {
                    throw new Error(data.message || 'Login failed. Please try again.');
                }
            }

            if (data.success && data.data) {
                this.accessToken = data.data.accessToken;
                this.refreshToken = data.data.refreshToken;

                localStorage.setItem('accessToken', data.data.accessToken);
                localStorage.setItem('refreshToken', data.data.refreshToken);
                localStorage.setItem('user', JSON.stringify(data.data.user));
            }

            return data;
        } catch (error: any) {
            console.error('Admin login error:', error);
            throw error;
        }
    }

    async login(email: string, password: string): Promise<LoginResponse> {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ADMIN_LOGIN}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            const data: LoginResponse = await response.json();

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Access denied. Admin privileges required.');
                } else if (response.status === 401) {
                    throw new Error(data.message || 'Invalid email or password');
                } else if (response.status === 429) {
                    throw new Error('Too many login attempts. Please try again later.');
                } else {
                    throw new Error(data.message || 'Login failed. Please try again.');
                }
            }

            if (data.success && data.data) {
                this.accessToken = data.data.accessToken;
                this.refreshToken = data.data.refreshToken;

                localStorage.setItem('accessToken', data.data.accessToken);
                localStorage.setItem('refreshToken', data.data.refreshToken);
                localStorage.setItem('user', JSON.stringify(data.data.user));
            }

            return data;
        } catch (error: any) {
            console.error('Admin login error:', error);
            throw error;
        }
    }

    async loginWithToken(code: string): Promise<LoginResponse> {
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGIN_WITH_TOKEN}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ code })
            });

            const data: LoginResponse = await response.json();

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Invalid or expired code. Please generate a new one from the mobile app.');
                } else if (response.status === 403) {
                    throw new Error('Account is not active. Please contact support.');
                } else {
                    throw new Error(data.message || 'Authentication failed. Please try again.');
                }
            }

            if (data.success && data.data) {
                this.accessToken = data.data.accessToken;
                this.refreshToken = data.data.refreshToken;

                localStorage.setItem('accessToken', data.data.accessToken);
                localStorage.setItem('refreshToken', data.data.refreshToken);
                localStorage.setItem('user', JSON.stringify(data.data.user));
            }

            return data;
        } catch (error: any) {
            console.error('Token login error:', error);
            throw error;
        }
    }

    async logout(): Promise<void> {
        try {
            if (this.accessToken) {
                await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGOUT}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.accessToken}`
                    }
                });
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.accessToken = null;
            this.refreshToken = null;
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
        }
    }

    getAccessToken(): string | null {
        return localStorage.getItem('accessToken');
    }

    getStoredUser(): User | null {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                return JSON.parse(userStr);
            } catch {
                return null;
            }
        }
        return null;
    }

    isAuthenticated(): boolean {
        return !!this.getAccessToken();
    }
}

export const authService = new AuthService();